import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  getOrganisationXeroAccounts,
  getOrganisationXeroTaxRate,
  getXeroTrackingCategories
} from "src/network/graphql/xeroServices";
import type { XeroAccount, XeroTaxType, XeroTrackingCategories } from "./xeroSlice";

const XERO_META_TTL_SECONDS = 300;

type XeroMetaData = {
  xeroAccounts: XeroAccount[];
  xeroTaxTypes: XeroTaxType[];
  xeroTrackingCategories: XeroTrackingCategories[];
};

export const xeroMetaApi = createApi({
  reducerPath: "xeroMetaApi",
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    getXeroMeta: builder.query<XeroMetaData, string>({
      async queryFn() {
        try {
          const accountsRes = await getOrganisationXeroAccounts();
          const taxRatesRes = await getOrganisationXeroTaxRate();
          const categoriesRes = await getXeroTrackingCategories();

          const xeroAccounts: XeroAccount[] = accountsRes.data?.get_organisation_xero_accounts ?? [];

          const rawTaxRates: XeroTaxType[] = taxRatesRes?.data?.get_organisation_xero_tax_rate ?? [];
          const xeroTaxTypes = Array.from(
            rawTaxRates
              .reduce((acc, item) => {
                const taxType = item?.taxType;

                if (!taxType || acc.has(taxType)) return acc;
                acc.set(taxType, {
                  taxType,
                  name: item?.name || taxType
                });
                return acc;
              }, new Map<string, XeroTaxType>())
              .values()
          );

          const xeroTrackingCategories: XeroTrackingCategories[] =
            categoriesRes.data?.get_organisation_xero_categories ?? [];

          return {
            data: {
              xeroAccounts,
              xeroTaxTypes,
              xeroTrackingCategories
            }
          };
        } catch (error) {
          return {
            error: error as unknown
          };
        }
      },
      keepUnusedDataFor: XERO_META_TTL_SECONDS
    })
  })
});

export const { useGetXeroMetaQuery } = xeroMetaApi;
export { XERO_META_TTL_SECONDS };
